import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { LanguageSelector } from "@/components/site/LanguageSelector";

const LINKS = [
  { key: "students", label: "For Learners", id: "about" },
  { key: "universities", label: "For Education Providers", to: "/partner" },
  { key: "how", label: "How It Works", id: "features" },
  { key: "vision", label: "Our Vision", id: "vision" },
  { key: "news", label: "News", to: "/news" },
  { key: "contact", label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const scroll = () => {
      const el = document.getElementById(id);
      if (el && lenis) lenis.scrollTo(el, { offset: -80 });
      else if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 300);
    } else scroll();
  };

  const handleLink = (l) => {
    if (l.to) {
      setOpen(false);
      navigate(l.to);
    } else goTo(l.id);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/70 border-b border-slate-200" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="OpenDegree home" className="flex items-center gap-2.5 shrink-0" onClick={() => lenis && lenis.scrollTo(0)}>
          <Logo markClass="h-10 w-10" textClass="text-xl" />
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.key}
              data-testid={`nav-link-${l.key}`}
              onClick={() => handleLink(l)}
              className="px-3.5 py-2 text-[15px] text-slatey hover:text-ink transition-colors duration-200 font-medium"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSelector testId="language-selector-public" />
          <Link to="/app/login" data-testid="nav-login" className="px-3.5 py-2 text-[15px] font-medium text-ink hover:text-cobalt transition-colors duration-200">Login</Link>
          <Link
            data-testid="nav-start-journey-btn"
            to="/journey"
            className="group inline-flex items-center gap-2 rounded-full bg-cobalt px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,76,250,0.28)] transition-transform duration-200 hover:scale-[1.03] active:scale-95"
          >
            Start Your Journey
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-ink"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200"
          >
            <div className="px-5 py-4 flex flex-col">
              {LINKS.map((l) => (
                <button
                  key={l.key}
                  data-testid={`nav-mobile-link-${l.key}`}
                  onClick={() => handleLink(l)}
                  className="py-3 text-left text-lg font-medium text-ink border-b border-slate-100 last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <LanguageSelector testId="language-selector-mobile" />
              <button
                data-testid="nav-mobile-start-journey-btn"
                onClick={() => { setOpen(false); navigate("/journey"); }}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-semibold text-white"
              >
                Start Your Journey <ArrowRight className="h-4 w-4" />
              </button>
              <button
                data-testid="nav-mobile-contact-btn"
                onClick={() => goTo("contact")}
                className="mt-3 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-ink"
              >
                Contact Us
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
